import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FaUserPlus,
  FaSearch,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getRecipientList } from "../../../../services/Recipients";
import { addNegotiationRecipient } from "../../../../services/CommonService";
import { toast } from "react-toastify";
import NegotiationRecipient from "../components/NegotiationRecipient";
import Utils from "../../../../utils";
// CHANGE: Import React-Select
import Select, { components } from 'react-select';

const NegotiationStep5 = ({ formObj = {}, setFormObj, setLoading, nextStep, prevStep }) => {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const selectRef = useRef(null);

  const [recipients, setRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [searchRecipientList, setSearchRecipientList] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  // Load existing recipients
  useEffect(() => {
    const saved = formObj?.recipient_List ?? [];
    setRecipients(saved);
  }, [formObj]);

  // Handle Search Input Change
  const handleRecipientInputChange = (inputValue) => {
    setRecipientSearch(inputValue);
    getRecipientList({ search: inputValue, limit: 50, page: 1 })
      .then((response) => {
        setSearchRecipientList(response.data?.data || []);
      })
      .catch(() => setSearchRecipientList([]));
  };

  // Handle Input Focus (Load initial list)
  const handleRecipientInputFocus = () => {
    if (recipientSearch === "" && searchRecipientList.length === 0) {
      getRecipientList({ search: "", limit: 50, page: 1 })
        .then((response) => {
          setSearchRecipientList(response.data?.data || []);
        })
        .catch(() => setSearchRecipientList([]));
    }
  };

  // Handle Selection
  const handleSelectRecipient = (selectedOptions) => {
    const options = selectedOptions || [];
    const newRecipientList = options.map(opt => opt.data);
    
    // Update local state and parent form object
    setRecipients(newRecipientList);
    
    const formData = { ...formObj };
    formData.recipient_List = newRecipientList;
    setFormObj(formData);
  };

  // Prepare Options for React-Select (Memoized to prevent scroll jump)
  const recipientOptions = useMemo(() => {
    return searchRecipientList.map(item => ({
      value: item.email,
      label: `${item.first_name} ${item.last_name} (${item.email})`,
      data: item,
    }));
  }, [searchRecipientList]);

  // Current Selected Value for React-Select
  const currentSelectedValue = recipientOptions.filter(option => 
    recipients.some(r => r.email === option.data.email)
  );

  // Custom Format Option Label (Inline & Responsive)
  const formatRecipientOption = (option) => {
    const isSelected = recipients.some(
      (r) => r.email === option.data.email
    );

    return (
      <div className="d-flex align-items-center w-100">
        {/* Avatar Circle */}
        <div className="recipient-search-avatar position-relative" style={{
          width: '32px',
          height: '32px',
          minWidth: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          fontSize: isSelected ? '1.2rem' : '14px',
          fontWeight: isSelected ? 'bold' : 'normal',
          backgroundColor: isSelected ? '#198754' : '#e9ecef', // Green if selected
          color: isSelected ? '#fff' : '#495057',               // White text if selected
          transition: 'all 0.2s ease'
        }}>
          {isSelected ? (
            <i className="bi bi-check-lg"></i>
          ) : (
            option.data.first_name?.[0]?.toUpperCase() || ""
          )}
        </div>

        {/* Details Section (Inline & Responsive) */}
        <div className="ms-3 d-flex flex-wrap align-items-center flex-grow-1" style={{ gap: '0 5px', lineHeight: '1.2' }}>
          <div className="fw-bold text-dark" style={{ whiteSpace: 'nowrap' }}>
            {option.data.first_name + " " + option.data.last_name}
          </div>
          <div className="fw-medium" style={{ fontSize: '14px', color: '#6c757d' }}>
             &lt;{option.data.email}&gt;
          </div>
          {option.data.mobile && (
            <div className="text-muted" style={{ fontSize: '14px' }}>
               | {Utils.mobileFormat(option.data)}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Custom Menu List with Sticky 'Done/Close' Button
  const CustomMenuList = useMemo(() => (props) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div style={{
          padding: '8px 10px',
          textAlign: 'right',
          borderTop: '1px solid #e3e8f0',
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 2,
          marginTop: 'auto'
        }}>
          <button
            className="btn btn-primary btn-sm"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              selectRef.current.blur();
            }}
          >
            Done / Close
          </button>
        </div>
      </components.MenuList>
    );
  }, []);

  // Remove recipient manually from list
  const handleRemoveRecipient = (index) => {
    const newList = [...recipients];
    newList.splice(index, 1);
    setRecipients(newList);
    
    const formData = { ...formObj };
    formData.recipient_List = newList;
    setFormObj(formData);
  };

  // Add new recipient manually
  const handleAddRecipient = () => {
    setSelectedRecipient(null);
    // Programmatically show offcanvas using Bootstrap API
    const offcanvasEl = document.getElementById('addRecipientsnegotiation');
    if (offcanvasEl) {
      const bsOffcanvas = window.bootstrap?.Offcanvas?.getOrCreateInstance(offcanvasEl);
      bsOffcanvas?.show();
    }
  };

  // Edit recipient
  const handleEditRecipient = (obj) => {
    setSelectedRecipient(obj);
    const offcanvasEl = document.getElementById('addRecipientsnegotiation');
    if (offcanvasEl) {
      const bsOffcanvas = window.bootstrap?.Offcanvas?.getOrCreateInstance(offcanvasEl);
      bsOffcanvas?.show();
    }
  };

  // Save & Exit
  const handleSaveExit = async (e) => {
    e.preventDefault();
    if (recipients.length === 0) {
      toast.error("Please add at least one collaborator");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        negotiation_id: formObj?.negotiation_id || formObj?.id,
        recipients: recipients.map((r) => ({
          recipient_id: r.id,
          email: r.email,
          role: 1,
        })),
        save_only: true,
      };
      await addNegotiationRecipient(payload);
      toast.success("Progress saved successfully!");
      navigate("/client-portal/negotiation-contract");
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Next Step
  const handleNext = async (e) => {
    e.preventDefault();
    if (recipients.length === 0) {
      toast.error("Please add at least one collaborator");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        negotiation_id: formObj?.negotiation_id || formObj?.id,
        recipients: recipients.map((r) => ({
          recipient_id: r.id,
          email: r.email,
          role: 1,
        })),
        save_only: false,
      };
      await addNegotiationRecipient(payload);
      toast.success("Collaborators added successfully!");
      nextStep && nextStep();
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Back
  const handleBack = () => {
    prevStep ? prevStep() : navigate(-1);
  };

  return (
    <div className="negotiation-container negotiation-step5">
      <div className="negotiation-wrapper step5-card">
        {/* Header */}
        <div className="negotiation-step1-header">
          <div className="negotiation-header-left">
            <h2 className="step1-title">Add Collaborators</h2>
            <p className="step1-subtitle">Add people who need to review or sign this negotiation.</p>
          </div>
          <div className="negotiation-header-right">
            <span className="step-indicator">Step 5 of 6</span>
          </div>
        </div>

        <div className="negotiation-main-card ">
          <div className="sleeve-sender-card mb-4">
            <div className="recipient-details-card-custom-add">
              <span className="fw-bold fs-5 Recipient-Details mb-2">
                Collaborator Details
              </span>
              <button
                type="button"
                className="btn-create ms-auto mb-2"
                ref={buttonRef}
                onClick={handleAddRecipient}
              >
                <FaUserPlus className="me-2" />
                Add Collaborator
              </button>
            </div>

            {/* 🔹 React-Select Component */}
            <div className="recipient-search mt-3">
               <Select
                  ref={selectRef}
                  classNamePrefix="sleeve-sender-select"
                  value={currentSelectedValue}
                  isMulti
                  closeMenuOnSelect={false}
                  controlShouldRenderValue={false}
                  hideSelectedOptions={false}
                  components={{ MenuList: CustomMenuList }}
                  maxMenuHeight={400} // FIXED HEIGHT 400px
                  inputValue={recipientSearch}
                  onInputChange={handleRecipientInputChange}
                  onFocus={handleRecipientInputFocus}
                  onChange={handleSelectRecipient}
                  options={recipientOptions}
                  placeholder="Search & Select Multiple Collaborators"
                  isClearable={false}
                  isSearchable
                  formatOptionLabel={formatRecipientOption}
                  styles={{
                      control: (base) => ({
                          ...base,
                          borderRadius: 6,
                          minHeight: 48,
                          borderColor: '#e3e8f0',
                          boxShadow: 'none',
                          background: '#fff'
                      }),
                      option: (base, state) => ({
                          ...base,
                          background: state.isFocused ? '#f3f4f6' : '#fff',
                          color: '#222',
                          cursor: 'pointer',
                          padding: '6px 12px',
                          borderBottom: '1px solid #f8f9fa'
                      }),
                      menu: (base) => ({ ...base, zIndex: 99 }),
                      input: (base) => ({ ...base, fontSize: '1rem' }),
                  }}
              />
            </div>

            {/* 🔹 Recipient List */}
            {recipients.length === 0 ? (
              <div className="recipient-empty-state my-4">
                <div className="empty-icon-wrapper">
                  <FaUsers />
                </div>
                <h5>No collaborators added yet</h5>
                <p>Add collaborators above to send your contract for review or signature.</p>
              </div>
            ) : (
              <div className="recipient-list-body mt-4">
                {recipients.map((r, i) => (
                  <div className="recipient-list-item" key={i}>
                    <div className="negotiation-recipient-info gap-3">
                      <div className="negotiation-recipient-avatar">
                        {r.first_name?.[0]?.toUpperCase() || ""}
                      </div>

                      <div className="negotiation-recipient-name">
                        {r.first_name} {r.last_name}
                      </div>
                      <div className="negotiation-recipient-email">{r.email}</div>
                      {r.mobile && (
                         <div className="negotiation-recipient-email text-muted">| {Utils.mobileFormat(r)}</div>
                      )}
                    </div>
                    <div className="recipient-list-actions">
                      <span
                        className="text-primary fs-5 cursor-pointer"
                        onClick={() => handleEditRecipient(r)}
                      >
                        <i className="bi bi-pencil-square" />
                      </span>
                      <span
                        className="text-danger fs-5 cur-pointer"
                        onClick={() => handleRemoveRecipient(i)}
                      >
                        <i className="bi bi-x-lg" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 🔹 Footer Buttons */}
          <div className="sleeve-step-buttons">
            <button className="sleeve-btn-back" onClick={handleBack}>
              Back
            </button>
            <div className="sleeve-step-buttons-right">
              <button className="sleeve-btn-save" onClick={handleSaveExit}>
                Save & Exit
              </button>
              <button className="sleeve-btn-next" onClick={handleNext}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🔹 Offcanvas Component */}
      <NegotiationRecipient
        formObj={formObj}
        setFormObj={setFormObj}
        setLoading={setLoading}
        recipData={selectedRecipient}
        setSelectedRecipient={setSelectedRecipient}
      />
    </div>
  );
};

export default NegotiationStep5;